/**
 * Feed lane seam implementations. Merged into `installCapabilities` by the
 * mount alongside the calendar and table lanes' files, so three lanes never
 * edit one file.
 *
 * Implements SEAM 11, `approveKeyword`. Calls the same service layer the feed
 * page uses — never a page handler, never a parallel HTTP path — because the
 * spec warns UI and tool paths can otherwise drift into different validation.
 */
import { contentPlannerService } from '@/services/contentPlannerService';

import type { KeywordFeedStatus } from '@/types/contentPlanner';

import {
  refuse,
  type CommitAuthorization,
  type KeywordCandidateSummary,
  type ReadDeps,
  type Receipt,
  type Refusal,
  type TenantId,
  type WriteDeps,
} from './contract';

/** The table this lane is allowed to write. Any other binding is a bug. */
const FEED_TABLE = 'keyword_feed_items';

/**
 * Turns a thrown transport error into a `Refusal`, because the convention here
 * is that refusals are values and exceptions are reserved for bugs. A 409 is
 * the server's own duplicate/approval gate and carries the reason the agent
 * needs, so it is surfaced rather than flattened into "request failed".
 */
function toRefusal(error: unknown, keywordId: string): Refusal {
  const response = (error as { response?: { status?: number; data?: { detail?: unknown } } })
    ?.response;
  const status = response?.status;
  const detail = response?.data?.detail;

  if (status === 409) {
    const reason =
      typeof detail === 'string'
        ? detail
        : typeof (detail as { reason?: string })?.reason === 'string'
          ? (detail as { reason: string }).reason
          : 'the server rejected the transition';
    return refuse(`Feed item ${keywordId} was refused by the server: ${reason}`, {
      retryable: false,
    });
  }
  if (status === 404) {
    return refuse(`Feed item ${keywordId} does not exist for this tenant.`, { retryable: false });
  }
  const message = error instanceof Error ? error.message : String(error);
  return refuse(`Approving ${keywordId} failed: ${message}`, { retryable: true });
}

/**
 * Checks the authorization actually covers this write. The runtime mints it,
 * but a seam that trusts it blindly would write under an expired or
 * mis-targeted grant, and the whole point of the one-use authorization is that
 * the writing step is the one place that cannot be reached by accident.
 *
 * `rowId` is null for a multi-row approval by construction (the diff sets it
 * only when exactly one row is targeted), so a null is accepted and a
 * MISMATCHED non-null is not.
 */
function authorizationVeto(auth: CommitAuthorization, keywordIds: readonly string[]): Refusal | null {
  if (auth.table !== FEED_TABLE) {
    return refuse(
      `Authorization is bound to ${auth.table}, not ${FEED_TABLE}; refusing to write outside it.`,
      { retryable: false },
    );
  }
  if (auth.rowId !== null && !(keywordIds.length === 1 && keywordIds[0] === auth.rowId)) {
    return refuse(`Authorization is bound to row ${auth.rowId}, not this set.`, {
      retryable: false,
    });
  }
  if (Number.isFinite(auth.expiresAt) && Date.now() > auth.expiresAt) {
    return refuse('Authorization expired before the write ran.', { retryable: true });
  }
  return null;
}

/**
 * SEAM 11. Approve keyword-feed candidates.
 *
 * Takes the whole set in one call because the authorization is one-use and is
 * consumed by the guarded wrapper on first entry; a per-row loop at the command
 * layer would refuse every row after the first.
 *
 * Deliberately narrow: status is the only field it sets. The caller cannot
 * reach target_month, duplicate_status or a verdict through this seam, so the
 * approve path can never be repurposed into a general feed writer.
 *
 * The backend fills `target_month` itself when a row has none
 * (keyword_feed_service.py:671-677). That is the server's behaviour, not a
 * field this seam sends.
 *
 * There is no transaction: the rows are patched one at a time, so a mid-set
 * failure reports which ids already landed rather than implying a rollback.
 */
async function approveKeyword(
  auth: CommitAuthorization,
  keywordIds: readonly string[],
): Promise<Receipt | Refusal> {
  const veto = authorizationVeto(auth, keywordIds);
  if (veto) return veto;
  if (keywordIds.length === 0) {
    return refuse('No keyword ids were supplied, so nothing was written.', { retryable: false });
  }

  const approved: string[] = [];
  for (const keywordId of keywordIds) {
    try {
      const updated = await contentPlannerService.updateKeywordFeedItem(
        keywordId,
        { status: 'approved' },
        auth.tenant,
      );
      // Never resolve a falsy success: a service that returns nothing has not
      // told us the row moved, and reporting a write that may not have
      // happened is worse than refusing.
      if (!updated) {
        return refuse(
          `Approving ${keywordId} returned no row; treating it as not written. ` +
            `Already approved: ${approved.length ? approved.join(', ') : 'none'}.`,
          { retryable: true },
        );
      }
      approved.push(String(updated.primary_keyword ?? keywordId));
    } catch (error) {
      const refusal = toRefusal(error, keywordId);
      return refuse(
        `${refusal.because} Already approved: ${approved.length ? approved.join(', ') : 'none'}.`,
        { retryable: refusal.retryable ?? false },
      );
    }
  }

  return {
    summary: `Approved ${approved.length} keyword${approved.length === 1 ? '' : 's'}: ${approved.join(', ')}.`,
    facts: { approved: approved.length, ids: [...keywordIds].sort().join(',') },
  };
}

/** Rows the agent should never be handed: already spent, or hidden by design. */
const LISTABLE_STATUSES: ReadonlySet<string> = new Set(['candidate', 'approved', 'stale']);

/**
 * SEAM 12. Read keyword-feed candidates.
 *
 * The backend accepts one status plus paging and nothing else, so text and
 * month narrowing happen here after the fetch rather than being faked into the
 * query string. `include_skipped` stays false: a skipped row cannot be
 * approved, so listing one would only invite a refusal.
 */
async function findKeywordCandidates(
  tenant: TenantId,
  query: Readonly<{
    status?: readonly string[];
    textContains?: string;
    targetMonth?: string;
    limit?: number;
  }>,
): Promise<readonly KeywordCandidateSummary[]> {
  const limit = Math.min(Math.max(query.limit ?? 25, 1), 100);
  const response = await contentPlannerService.listKeywordFeed(
    {
      // One status only server-side; a multi-status request is narrowed below.
      ...(query.status?.length === 1
        ? { status: query.status[0] as KeywordFeedStatus }
        : {}),
      include_skipped: false,
      limit: 100,
    },
    tenant,
  );

  const wanted = query.status?.length ? new Set(query.status) : null;
  const needle = query.textContains?.trim().toLowerCase();

  return (response.items ?? [])
    .filter((item) => LISTABLE_STATUSES.has(String(item.status)))
    .filter((item) => (wanted ? wanted.has(String(item.status)) : true))
    .filter((item) =>
      needle ? String(item.primary_keyword ?? '').toLowerCase().includes(needle) : true,
    )
    .filter((item) => (query.targetMonth ? item.target_month === query.targetMonth : true))
    .slice(0, limit)
    .map((item) => ({
      id: String(item.id),
      keyword: String(item.primary_keyword ?? ''),
      status: String(item.status ?? ''),
      duplicateStatus: String(item.duplicate_status ?? ''),
      verdict: item.hermes_verdict == null ? null : String(item.hermes_verdict),
      targetMonth: item.target_month == null ? null : String(item.target_month),
    }));
}

/**
 * The feed lane's contribution to the capability set. Exported as a partial so
 * the mount can merge it with the other lanes' seams without any lane needing
 * to know what the others implement.
 */
export const feedSeams: Partial<ReadDeps & WriteDeps> = {
  approveKeyword,
  findKeywordCandidates,
};

/** The mount merges seam files by their default export. */
export default feedSeams;
